import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type ViewCategoryStore from '../../AppData/_stores/ViewCategoryStore';
import { getCanonicalLocale } from '../../Common/SEO/SeoLinks';
import { useAppSelector } from '../../Common/_hooks/useAppSelector';

const compareCategoryOrder = (a: ViewCategoryStore, b: ViewCategoryStore) => {
  return a.get('theorder') - b.get('theorder');
}

export default function useProductCatalogCategories() {
  const [, routesI18n] = useTranslation('routes');
  const viewCategories = useAppSelector(state => state.get('appData').get('viewCategories'));
  const currentLocale = getCanonicalLocale(routesI18n.resolvedLanguage ?? routesI18n.language);

  return useMemo(() => {
    const categoriesById = new Map<number, ViewCategoryStore>();
    const topLevelCategoriesByUrlSlug = new Map<string, ViewCategoryStore>();
    const childCategoriesByParentIdAndUrlSlug = new Map<number, Map<string, ViewCategoryStore>>();
    const childrenByParentId = new Map<number, ViewCategoryStore[]>();
    const topLevelCategories: ViewCategoryStore[] = [];
    const getCategoryUrlSlug = (category: ViewCategoryStore) => {
      return category.get('url_slugs').get(currentLocale) ?? category.get('url_slug');
    }

    viewCategories.forEach(category => {
      categoriesById.set(category.get('id'), category);

      const parentId = category.get('id_parent');
      if (parentId === null) {
        topLevelCategories.push(category);
        topLevelCategoriesByUrlSlug.set(getCategoryUrlSlug(category), category);
        return;
      }

      const childCategories = childrenByParentId.get(parentId) ?? [];
      childCategories.push(category);
      childrenByParentId.set(parentId, childCategories);

      const childCategoriesByUrlSlug = childCategoriesByParentIdAndUrlSlug.get(parentId) ?? new Map<string, ViewCategoryStore>();
      childCategoriesByUrlSlug.set(getCategoryUrlSlug(category), category);
      childCategoriesByParentIdAndUrlSlug.set(parentId, childCategoriesByUrlSlug);
    });

    topLevelCategories.sort(compareCategoryOrder);
    childrenByParentId.forEach(childCategories => childCategories.sort(compareCategoryOrder));

    return {
      categoriesById,
      childCategoriesByParentIdAndUrlSlug,
      childrenByParentId,
      getCategoryUrlSlug,
      topLevelCategories,
      topLevelCategoriesByUrlSlug,
    };
  }, [currentLocale, viewCategories]);
}
